import React from "react";
import { IconButton } from "../../../../ui/components/IconButton";
import { FeatherX } from "@subframe/core";
import { formatDateDDMMYYYY } from "../../../../shared/utils/formatters";
import { TrialBalanceTransaction } from "../../data/api/trialBalanceApi";

interface TrialBalanceTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  loading: boolean;
  error?: string;
  transactions: TrialBalanceTransaction[];
  openingBalance?: number | null;
  closingBalance?: number | null;
}

const formatAmount = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "-";
  }
  return new Intl.NumberFormat("en-IN").format(value);
};

export const TrialBalanceTransactionsModal: React.FC<TrialBalanceTransactionsModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  loading,
  error,
  transactions,
  openingBalance = null,
  closingBalance = null,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 p-6">
      <div className="flex flex-col w-full max-w-[1200px] h-[80vh] bg-default-background overflow-hidden rounded-lg shadow-xl border border-neutral-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-border bg-neutral-50">
          <div className="flex flex-col">
            <h2 className="text-heading-2 font-heading-2 text-default-font">{title}</h2>
            {subtitle && (
              <span className="text-caption font-caption text-subtext-color">{subtitle}</span>
            )}
          </div>
          <IconButton icon={<FeatherX />} onClick={onClose} size="medium" />
        </div>

        <div className="flex-1 overflow-auto bg-white">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-subtext-color">Loading transactions...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-error-600">{error}</span>
            </div>
          ) : transactions.length === 0 &&
            openingBalance === null &&
            closingBalance === null ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-subtext-color">No transactions found.</span>
            </div>
          ) : (
            <table className="border-collapse min-w-full">
              <thead className="sticky top-0 z-20">
                <tr>
                  <th className="px-3 py-2 text-left text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    Date
                  </th>
                  <th className="px-3 py-2 text-left text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    Type
                  </th>
                  <th className="px-3 py-2 text-left text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    Reference
                  </th>
                  <th className="px-3 py-2 text-left text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    Payee
                  </th>
                  <th className="px-3 py-2 text-left text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    Description
                  </th>
                  <th className="px-3 py-2 text-right text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    Debit
                  </th>
                  <th className="px-3 py-2 text-right text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    Credit
                  </th>
                </tr>
              </thead>
              <tbody>
                {openingBalance !== null && (
                  <tr className="bg-neutral-50">
                    <td className="px-3 py-2 text-body font-body border border-neutral-200 font-semibold">
                      Opening Balance
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                    <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                    <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                    <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                    <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                      {openingBalance >= 0 ? formatAmount(openingBalance) : "-"}
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                      {openingBalance < 0 ? formatAmount(Math.abs(openingBalance)) : "-"}
                    </td>
                  </tr>
                )}
                {transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-neutral-50">
                    <td className="px-3 py-2 text-body font-body border border-neutral-200">
                      {formatDateDDMMYYYY(tx.transaction_date) || "-"}
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200">
                      {tx.transaction_type || "-"}
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200">
                      {tx.reference_number || tx.entry_number || "-"}
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200">
                      {tx.payee || "-"}
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200 max-w-[360px] truncate">
                      {tx.description || "-"}
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                      {formatAmount(tx.debit_amount ?? null)}
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                      {formatAmount(tx.credit_amount ?? null)}
                    </td>
                  </tr>
                ))}
                {closingBalance !== null && (
                  <tr className="bg-neutral-50">
                    <td className="px-3 py-2 text-body font-body border border-neutral-200 font-semibold">
                      Closing Balance
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                    <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                    <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                    <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                    <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                      {closingBalance >= 0 ? formatAmount(closingBalance) : "-"}
                    </td>
                    <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                      {closingBalance < 0 ? formatAmount(Math.abs(closingBalance)) : "-"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
